/**TimeService's goal is to have a single place for all
 * time functionality in Admin portal
 *
 * All functions should work with and return (where appropriate) the Javascript
 * Date type
 */
// eslint-disable-next-line no-restricted-imports
import { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import pluralize from 'pluralize';
import dayjs from '~/timeInit';

dayjs.extend(utc);
dayjs.extend(timezone);

export const SYDNEY_TIMEZONE = 'Australia/Sydney';

/** Export Dayjs type */
export { Dayjs };

/**Returns dates in format: 20260224 */
export function formatDateYYYYMMDD(value: Dayjs): string {
  return value.format('YYYYMMDD');
}

/**Returns dates in format: Thursday */
export function formatDateFullDay(value?: Date | string): string {
  return dayjs(value).format('dddd');
}

/**Returns dates in format: 2018-Sep*/
export function formatDateReportsByMonth(value?: Date): string {
  return dayjs(value).format('YYYY-MMM');
}

/**Returns dates in format: 2018-Sep-08*/
export function formatDateReportsByDate(value?: Date): string {
  return dayjs(value).format('YYYY-MMM-DD');
}

/**Returns dates in format: September*/
export function formatDateMonth(value?: Date): string {
  return dayjs(value).format('MMMM');
}

/**Returns dates in format: Thu*/
export function formatDateDayShortDayName(value?: Date): string {
  return dayjs(value).format('ddd');
}

/**Returns dates in format: 26*/
export function formatDateDay(value?: Date): string {
  return dayjs(value).format('D');
}

/**Returns dates in format: September 1994 */
export function formatDateCalendarTitle(value?: Date): string {
  return dayjs(value).format('MMMM YYYY');
}

/**Returns dates in format: 1994-04 */
export function formatDateCalendar(value?: Date): string {
  return dayjs(value).format('YYYY-MM');
}

/**Returns dates in format:  September 4, 1986 8:30 PM */
export function formatDateLongMonthWithMeridiemNoSeconds(value?: Date | string): string {
  return dayjs(value).format('LLL');
}

/**Returns dates in format: 2022-09-12-16-24 */
export function formatDateISOWithTime(value?: Date): string {
  return dayjs(value).format('YYYY-MM-DD-HH-mm');
}

/**Returns dates in format: September 12, 2022 4:22:31 PM */
export function formatDateLongMonthWithMeridiem(value?: Date | string): string {
  return dayjs(value).format('LL LTS');
}

/**Returns dates in format: 22 Mar 2024, 22:02:30 */
export function formatDateLongMonthWithMeridiemShort(value?: Date | string): string {
  return dayjs(value).format('DD MMM YYYY, HH:mm:ss');
}

/**Returns dates in format: 23/03/2025 2:34pm */
export function formatDateSlashWithTime(value?: Date | string): string {
  return dayjs(value).format('DD/MM/YYYY h:mma');
}

/**Returns dates in format: Monday, September 12th 2022 */
export function formatDateFormal(value?: Date): string {
  return dayjs(value).format('dddd, MMMM Do YYYY');
}

/** Returns dates in format: 6 January 2018 */
export function formatDateLong(value?: Date | string | number): string {
  return dayjs(value).format('D MMMM YYYY');
}

export function formatDateShortDateAndMonth(value?: Date): string {
  return dayjs(value).format('D MMM');
}

/**Returns dates in format: 31st Jan 1994 16:23:21 */
export function formatDateOrdinalWithClock(value?: Date | string): string {
  return dayjs(value).format('Do MMM YYYY HH:mm:ss');
}

export function formatDateOrdinalWithClockInSydney(value?: Date | string): string {
  return dayjs.utc(value).tz(SYDNEY_TIMEZONE).format('Do MMM YYYY HH:mm:ss');
}

/**Returns dates in format: 1973-03-03 */
export function formatDateISO(value?: Date | string): string {
  return dayjs(value).format('YYYY-MM-DD');
}

/** Parses `value` in the local timezone, then converts to UTC. Returns dates in format: 1973-03-03 */
export function formatLocalDateISOUTC(value?: Date | string): string {
  return dayjs(value).utc().format('YYYY-MM-DD');
}

/** Parses `value` as UTC directly — use for comparing/keying bare date strings. */
export function formatDateISOUTC(value: string): string {
  return dayjs.utc(value).format('YYYY-MM-DD');
}

/**Returns dates in format: 2025-09-12T01:59:00+00:00Z just for DateTimeWidget */
export function formatDateDateTimeWidget(value?: Date): string {
  return dayjs(value).format('YYYY-MM-DDTHH:mm:ss[Z]');
}

/**Returns dates in format: 2025-09-12T01:59:00+00:00Z just for DateTimeWidget in UTC */
export function formatUTCDateDateTimeWidget(value?: Date): string {
  return dayjs.utc(value).format('YYYY-MM-DDTHH:mm:ss[Z]');
}

/** Returns dates in format: 2022-08-18T19:45:55+10:00 */
export function formatDateLongISO(value?: Date): string {
  return dayjs(value).format('YYYY-MM-DDTHH:mm:ssZ');
}

/**Returns dates in format: 02 Jan 1970 13:46 */
export function formatDateWithClock(value?: Date | string | Dayjs): string {
  return dayjs(value).format('DD MMM YYYY HH:mm');
}

/**Returns dates in format: 02-Jan 13:46 */
export function formatDateShortMonthWithClock(value?: Date | string | Dayjs): string {
  return dayjs(value).format('DD-MMM HH:mm');
}

/**Returns dates in format: 09 Jan 1994 */
export function formatDateShortDD(value?: Date | string | number): string {
  return dayjs(value).format('DD MMM YYYY');
}

/**Returns dates in format: 12 Aug 25 */
export function formatDateDDMonYY(value?: Date | string | number): string {
  return dayjs(value).format('DD MMM YY');
}

/**Returns dates in format: 9 Jan 1994 */
export function formatDateShort(value?: Date | string): string {
  return dayjs(value).format('D MMM YYYY');
}

/** Returns dates in format: 2 Jan 1970, 13:46 */
export function formatDateShortWithClock(value?: Date | string): string {
  return dayjs(value).format('D MMM YYYY, HH:mm');
}

/** Returns local datetime input values in format: 1970-01-02T13:46 */
export function formatDateTimeLocal(value?: Date | string): string {
  return dayjs(value).format('YYYY-MM-DDTHH:mm');
}

/**Returns dates in format: 14th Apr 1970 */
export function formatDateOrdinalShortMonth(value?: Date): string {
  return dayjs(value).format('Do MMM YYYY');
}

/**Returns dates in format: 03-03-1994 */
export function formatDateNumeralMonths(value?: Date | string): string {
  return dayjs(value).format('DD-MM-YYYY');
}

/**Returns dates in format: Thu 1 Jan 1970  */
export function formatDateWeekDayShortMonth(value?: Date | string): string {
  return dayjs(value).format('ddd D MMM YYYY');
}

/**Returns dates in format: 01/01/1970 */
export function formatDateSlashes(value?: Date | string): string {
  return dayjs(value).format('DD/MM/YYYY');
}

/**Returns dates in format: 01/01/1970 13:46 */
export function formatDateSlashesWithClock(value?: Date | string): string {
  return dayjs(value).format('DD/MM/YYYY HH:mm');
}

export function formatDateSlashesWithClockInSydney(value?: Date | string): string {
  return dayjs(value).tz(SYDNEY_TIMEZONE).format('DD/MM/YYYY HH:mm');
}

export function formatDateMMYY(value?: Date | string): string {
  return dayjs(value).format('MM/YY');
}

/** takes date and its format to create correct date object */
export function newDate(value?: Date | string, format = 'YYYY-MM-DD') {
  return dayjs(value, format);
}

/** Parse a value into Dayjs (e.g. ISO strings); invalid input yields non-isValid Dayjs. */
export function parseDayjs(value?: Date | string | null): Dayjs {
  return dayjs(value ?? undefined);
}

export function parseDayjsInTimezone(value: string, timezone: string): Dayjs {
  return dayjs.tz(value, timezone);
}

export function parseValidDayjs(value?: string | null): Dayjs | undefined {
  return value ? parseDayjs(value) : undefined;
}

/** Timestamp for chronological sorting, or null for empty/unparseable input (so it sorts last). */
export function toSortableTimestamp(value?: string | null): number | null {
  if (!value) {
    return null;
  }
  const date = dayjs(value);
  return date.isValid() ? date.valueOf() : null;
}

/** Safely parses `value` into a validated `Date`, or `undefined` if empty/unparseable. */
export function parseValidDate(value?: string | null): Date | undefined {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Returns true if between_date is inbetween the two dates
 *
 * between_date defaults to dateNow's value if no argument is provided
 *
 * @from_date from_date < to_date
 * @to_date
 *
 * @inclusionDescriptor https://day.js.org/docs/en/plugin/is-between
 */
export function isBetween(
  from_date: string | Date,
  to_date: string | Date,
  between_date?: string | Date,
  granularity?: dayjs.OpUnitType,
  inclusionDescriptor: '()' | '[]' | '[)' | '(]' = '()',
): boolean {
  if (!between_date) {
    return dayjs().isBetween(from_date, to_date);
  }
  return dayjs(between_date).isBetween(from_date, to_date, granularity, inclusionDescriptor);
}

/** Takes in a TimeValue and returns a Date
 *
 * Returns null if timestamp is null/undefined
 *
 * https://tc39.es/ecma262/#sec-time-values-and-time-range
 */
export function unixToDate(timeStamp: number): Date {
  if (timeStamp === undefined || timeStamp === null) {
    return null;
  }
  return dayjs.unix(timeStamp).toDate();
}

/** Takes in a date and returns its TimeValue
 *
 * Defaults to dateNow()'s output if no input
 */
export function unix(date?: Date | Dayjs | string): number {
  return dayjs(date).unix();
}

/** Converts inputted date to local time
 *
 * Returns current local date and time if no argument is provided.
 */
export function dateNow(date?: Date | string): Date {
  return dayjs(date).toDate();
}

/** Converts inputted date to UTC time
 *
 * Returns current date and time in UTC if no argument is provided.
 */
export function dateNowUtc(date?: Date | string): Date {
  return dayjs.utc(date).toDate();
}

/** checks if two dates have same day, month and year */
export function datesHaveSameDayMonthYear(date1: Date | string, date2?: Date | string): boolean {
  return dayjs.utc(date1).isSame(dayjs.utc(date2), 'day');
}

/** Returns true if date1 > date2
 *
 * date2 will default to the value of dateNow() if no input
 */
export function isAfter(date1: Date | string, date2?: Date | string): boolean {
  return dayjs.utc(date1).isAfter(dayjs.utc(date2));
}

/** Returns true if `value` is a non-empty string that parses to a real date. */
export function isValidDateString(value: string | null | undefined): value is string {
  return !!value && dayjs(value).isValid();
}

/** Returns true if date1 < date2
 *
 * date2 will default to the value of dateNow() if no input
 */
export function isBefore(date1: Date | string, date2?: Date | string): boolean {
  return dayjs.utc(date1).isBefore(dayjs.utc(date2));
}

/** Returns true if date1 <= date2
 *
 * date2 will default to the value of dateNow() if no input
 */
export function isBeforeOrSame(date1: Date | string, date2?: Date | string): boolean {
  return isBefore(date1, date2) || dayjs.utc(date1).isSame(dayjs.utc(date2));
}

/** Returns the timezone offset in hours between
 * the client's timezone and UTC.
 *
 * It is positive if the local time is ahead, and vice-versa. */
export function getTimezoneOffset(): number {
  return (new Date().getTimezoneOffset() / 60) * -1;
}

/** Adds the number of hours to the date
 *
 * Defaults to dateNow()'s date if no date input
 */
export function addHours(hoursToAdd: number, date?: Date | string): Date {
  //double return is because date is not assignable to dayjs type
  if (!date) {
    return dayjs().add(hoursToAdd, 'hour').toDate();
  }
  return dayjs(date).add(hoursToAdd, 'hour').toDate();
}

/** Adds the number of minutes to the date
 *
 * Defaults to dateNow()'s date if no date input
 */
export function addMinutes(minutesToAdd: number, date?: Date | string): Date {
  //double return is because date is not assignable to dayjs type
  if (!date) {
    return dayjs().add(minutesToAdd, 'minute').toDate();
  }
  return dayjs(date).add(minutesToAdd, 'minute').toDate();
}

/** Subtracts the number of hours of a date
 *
 * Defaults to dateNow()'s date if no date input
 */
export function subtractHours(hoursToSubtract: number, date?: Date | string): Date {
  //double return is because date is not assignable to dayjs type
  if (!date) {
    return dayjs().subtract(hoursToSubtract, 'hour').toDate();
  }
  return dayjs(date).subtract(hoursToSubtract, 'hour').toDate();
}

/** Adds the number of days to the date
 *
 * Defaults to dateNow()'s date if no date input
 */
export function addDays(daysToAdd: number, date?: Date | string): Date {
  //double return is because date is not assignable to dayjs type
  if (!date) {
    return dayjs().add(daysToAdd, 'day').toDate();
  }
  return dayjs(date).add(daysToAdd, 'day').toDate();
}

/**
 * Adds the number of days to the date
 *
 * @param days The number of days to add
 * @param date The date to add days to. If not provided, the current date is used.
 * @returns The new date (DayJs object) with the number of days added.
 */
export function addDaysRaw(days: number, date?: string | number | Date | Dayjs): Dayjs {
  return dayjs(!date ? undefined : date).add(days, 'day');
}

/** Adds the number of months to the date
 *
 * Defaults to dateNow()'s date if no date input
 */
export function addMonths(monthsToAdd: number, date?: Date | string): Date {
  if (!date) {
    return dayjs().add(monthsToAdd, 'month').toDate();
  }
  return dayjs(date).add(monthsToAdd, 'month').toDate();
}

/** Adds the number of years to the date
 *
 * Defaults to dateNow()'s date if no date input
 */
export function addYears(yearsToAdd: number, date?: Date | string): Date {
  if (!date) {
    return dayjs().add(yearsToAdd, 'year').toDate();
  }
  return dayjs(date).add(yearsToAdd, 'year').toDate();
}

/** Adds duration
 *
 * Defaults to dateNow()'s date if no date input
 */
export function addDuration(durationOptions: Record<string, number>, date?: Date | string): Date {
  if (!date) {
    return dayjs().add(dayjs.duration(durationOptions)).toDate();
  }
  return dayjs(date).add(dayjs.duration(durationOptions)).toDate();
}

/** Subtracts the number of days from the date
 *
 * Defaults to dateNow()'s date if no date input
 */
export function subDays(daysToSubtract: number, date?: Date | string): Date {
  //double return is because date is not assignable to dayjs type
  if (!date) {
    return dayjs().subtract(daysToSubtract, 'day').toDate();
  }
  return dayjs(date).subtract(daysToSubtract, 'day').toDate();
}

/** Subtracts the number of days from the date
 *
 * Defaults to dateNow()'s date if no date input
 */
export function subMonths(monthsToSubtract: number, date?: Date | string): Date {
  if (!date) {
    return dayjs().subtract(monthsToSubtract, 'month').toDate();
  }
  return dayjs(date).subtract(monthsToSubtract, 'month').toDate();
}

/**Calculates the number of days difference between 2 dates
 *
 * Second date defaults to dateNow() if no input
 */
export function diffDays(
  date1: Date | string,
  date2?: Date | string,
  unitOfTime: dayjs.UnitType = undefined,
  decimal = false,
): number {
  if (!date2) {
    return dayjs(date1).diff(dateNow(), unitOfTime, decimal);
  }
  return dayjs(date1).diff(date2, unitOfTime, decimal);
}

// Whole-day count spanning both endpoints, e.g. a range starting and ending the same day is 1 day.
export function diffDaysInclusive(start: Date | string, end: Date | string): number {
  return diffDays(end, start, 'day') + 1;
}

/** Sets time to 23:59:59
 *
 * Defaults to dateNow()'s date if no date input
 */
export function endOfDay(date?: Date | string): Date {
  if (!date) {
    return dayjs().endOf('day').toDate();
  }
  return dayjs(date).endOf('day').toDate();
}

/** Sets time to current time if today, otherwise to 23:59:59
 *
 * Defaults to dateNow()'s date if no date input
 */
export function endOfDayOrEndOrCurrentTime(date?: Date | string): Date {
  const inputDate = dayjs(date);

  if (!date || inputDate.isSame(dayjs(), 'day')) {
    return dayjs().toDate();
  }

  return dayjs(date).endOf('day').toDate();
}

/** Sets time to 00:00:00
 *
 * Defaults to dateNow()'s date if no date input
 */
export function startOfDay(date?: Date | string): Date {
  if (!date) {
    return dayjs().startOf('day').toDate();
  }
  return dayjs(date).startOf('day').toDate();
}

/** Sets day to 0
 *
 * Defaults to dateNow()'s date if no date input
 */
export function startOfMonth(date?: Date | string): Date {
  if (!date) {
    return dayjs().startOf('month').toDate();
  }
  return dayjs(date).startOf('month').toDate();
}

/** Creates a 2D array of arrays representing the weeks of a month
 * for use in the calendar representation of a month
 *
 * https://www.timeanddate.com/calendar/?year=1999&country=29
 *
 * The above website would use a similar algorithm to generate the
 * layout of the days of each month, notice that February of that year takes up
 * 4 rows with Monday as the first day of the week
 *
 * Algorithm taken from https://github.com/react-dates/react-dates/blob/master/src/utils/getCalendarMonthWeeks.js
 */
export function getCalendarMonthWeeks(date, enableOutsideDays) {
  const firstDayOfWeek = dayjs.localeData().firstDayOfWeek();
  const month = dayjs(date);

  // set utc offset to get correct dates in future (when timezone changes)
  const firstOfMonth = month.clone().startOf('month').hour(12);
  const lastOfMonth = month.clone().endOf('month').hour(12);

  // calculate the exact first and last days to fill the entire matrix
  // (considering days outside month)
  const prevDays = (firstOfMonth.day() + 7 - firstDayOfWeek) % 7;
  const nextDays = (firstDayOfWeek + 6 - lastOfMonth.day()) % 7;
  const firstDay = firstOfMonth.clone().subtract(prevDays, 'day');
  const lastDay = lastOfMonth.clone().add(nextDays, 'day');

  const totalDays = lastDay.diff(firstDay, 'days') + 1;

  let currentDay = firstDay.clone();
  const weeksInMonth = [];

  for (let i = 0; i < totalDays; i += 1) {
    if (i % 7 === 0) {
      weeksInMonth.push([]);
    }

    let day = null;
    if ((i >= prevDays && i < totalDays - nextDays) || enableOutsideDays) {
      day = currentDay.clone();
    }

    weeksInMonth[weeksInMonth.length - 1].push(day ? day.format() : null);

    currentDay = currentDay.add(1, 'day');
  }

  return weeksInMonth;
}

/** Checks that the string is in format DD/MM/YYYY and is valid */
export function validateDateOfBirth(value: string) {
  if (value.length !== 10) {
    return {
      valid: false,
      date: null,
    };
  }

  const m = dayjs(value, 'DD/MM/YYYY', true);

  if (m.isValid()) {
    return {
      valid: true,
      date: formatDateISO(value),
    };
  }

  return {
    valid: false,
    date: null,
  };
}

export function rangeOverlap(aStart, aEnd, bStart, bEnd) {
  return dayjs.max(dayjs(aStart), dayjs(bStart)) < dayjs.min(dayjs(aEnd), dayjs(bEnd));
}

// rangeOverlap treats end dates as exclusive; this bridges the inclusive end_date both
// surcharges and blackout ranges actually use, so two ranges sharing a boundary day overlap.
export function rangeOverlapInclusive(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return rangeOverlap(
    aStart,
    dayjs(aEnd).add(1, 'day').format('YYYY-MM-DD'),
    bStart,
    dayjs(bEnd).add(1, 'day').format('YYYY-MM-DD'),
  );
}

export function sortByDate(a: string, b: string) {
  return unix(newDate(b, 'DD/MM/YYYY')) - unix(newDate(a, 'DD/MM/YYYY'));
}

export function getDateStartOf(unit: dayjs.OpUnitType, format = 'YYYY-MM-DD', timezone = 'Australia/Sydney'): string {
  return dayjs().tz(timezone).startOf(unit).format(format);
}

export function toDayJs(date?: Date | string): Dayjs {
  return dayjs(date);
}

export function getTimeDifference(
  bookByDate: Date,
  currentDate: Date,
  includeHoursMinsSecs = false,
  includeHistorical = false,
) {
  const timeDifference = dayjs.duration(dayjs(bookByDate).diff(dayjs(currentDate)));
  let years = timeDifference.years();
  let months = timeDifference.months();
  let days = timeDifference.days();
  let hours = timeDifference.hours();
  let minutes = timeDifference.minutes();
  let seconds = timeDifference.seconds();

  if (includeHistorical) {
    // if historical, we want to show the absolute value of the time difference
    years = Math.abs(years);
    months = Math.abs(months);
    days = Math.abs(days);
    hours = Math.abs(hours);
    minutes = Math.abs(minutes);
    seconds = Math.abs(seconds);
  }

  if (includeHoursMinsSecs) {
    return `${years > 0 ? `${years} ${pluralize('year', years)} ` : ''}${
      months > 0 ? `${months} ${pluralize('month', months)} ` : ''
    }${days > 0 ? `${days} ${pluralize('day', days)} ` : ''}${
      hours > 0 ? `${hours} ${pluralize('hour', hours)} ` : ''
    }${minutes > 0 ? `${minutes} ${pluralize('minute', minutes)} ` : ''}${
      seconds > 0 ? `${seconds} ${pluralize('second', seconds)}` : ''
    }`;
  }
  return `${years > 0 ? `${years} ${pluralize('year', years)} ` : ''}${
    months > 0 ? `${months} ${pluralize('month', months)} ` : ''
  }${days > 0 ? `${days} ${pluralize('day', days)}` : ''}`;
}

export const formatSeconds = (seconds: number) => {
  if (seconds === 0) {
    return '-';
  }

  const totalHours = Math.floor(seconds / (60 * 60));
  const leftoverSeconds = seconds - totalHours * 60 * 60;

  const totalMinutes = Math.floor(leftoverSeconds / 60);
  const totalSeconds = leftoverSeconds - totalMinutes * 60;

  return `${totalHours}h ${totalMinutes}m ${totalSeconds}s`;
};

/**
 * Creates a new DayJs instance based on the input date.
 */
export function getDayJs(date?: string | number | Date | Dayjs) {
  return dayjs(date);
}

/**
 * Builds a YYYY-MM-DD range ending today for a "Last N days/months" preset.
 * Used by dashboards that expose Last-2-weeks / Last-4-weeks / Last-3-months
 * style quick filters.
 */
export function buildDatePresetRange(amount: number, unit: 'day' | 'month'): { startAt: string; endAt: string } {
  const end = getDayJs();
  return {
    startAt: end.subtract(amount, unit).format('YYYY-MM-DD'),
    endAt: end.format('YYYY-MM-DD'),
  };
}

/**
 * Generate list of Date objects between two given dates
 * e.g.
 * [
 *  "2024-11-09T00:00:00.000Z"
 * ]
 */
export function generateDateRange(earlierDate: Date, laterDate: Date): Array<Date> {
  // Step in UTC: callers key nights by the toISOString() date, and a local-time step across a
  // daylight saving change lands on the previous UTC day, giving two nights the same key.
  let tempDate = dayjs.utc(earlierDate);
  const dateArray: Array<Date> = [];
  while (!tempDate.isSame(laterDate, 'day')) {
    dateArray.push(tempDate.toDate());
    tempDate = tempDate.add(1, 'day');
  }
  return dateArray;
}

/**
 * Guess local browsers timezone, and return formatted timezone offset
 * @returns GMT+9
 */
export function getBrowserTimeZoneAbbreviation() {
  return dayjs().tz(dayjs.tz.guess()).format('z');
}

/**
 * Returns the absolute difference in seconds between two dates
 * @param date1 First date to compare
 * @param date2 Second date to compare
 * @returns Number of seconds between the dates
 */
export function diffSeconds(date1: Date | string, date2: Date | string) {
  return Math.abs(dayjs(date1).diff(dayjs(date2), 'second'));
}

/**
 *
 * @param timezone
 * @returns The current date and time in the format 'Mon, Apr 07, 06:52 PM'
 * after being passed the timezone string from salesforce in the format 'Hawaii (UTC -10:00)'
 */
export const getCurrentTimeInTimezone = (timezone: string) => {
  const utcOffsetMatch = timezone.match(/UTC\s*([+-]\d+):\d+/);
  const utcOffset = utcOffsetMatch ? utcOffsetMatch[1] : '0';

  const dayjsTimezone = `UTC${utcOffset}`;

  return dayjs().tz(dayjsTimezone).format('ddd, MMM DD, hh:mm A');
};

/**
 * Offsets a date by a given number of minutes
 * @param date The date to offset
 * @param offset The number of minutes to offset the date by
 * @returns The offset date
 */
export const offsetUTC = (date: Date | string, offsetMinutes: number) => {
  return dayjs(date).add(offsetMinutes, 'minutes').toDate();
};

/**
 * Returns the earliest (minimum) date from a list of provided dates.
 *
 * @param {...(string | Date)} dates - One or more date values, each provided as either a Date object or a string
 *   that can be parsed by dayjs.
 * @returns {dayjs.Dayjs} The earliest date among the provided values, returned as a Dayjs object.
 */
export function minDate(...dates: Array<string | Date>): dayjs.Dayjs {
  const dayjsDates = dates.map((d) => dayjs(d));
  return dayjs.min(dayjsDates);
}

/**
 * Returns the list of months in the current locale
 * @returns Array of month names
 */
export function getMonths(): Array<string> {
  return dayjs.months();
}
